import styles from "./request-entry.module.css";

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  App,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Skeleton,
  Tag,
  Typography,
} from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";

import { cancelMyRequest, getRequestEntry } from "@/api/requests";

import { formateISODateToMoment } from "@/utils";

import routes from "@/routes";

const { Title, Text, Paragraph } = Typography;

const STATUS_CONFIG = {
  registered: {
    label: "Зарегистрирована",
    color: "blue",
  },
  completed: {
    label: "Выполнена",
    color: "green",
  },
  cancelled: {
    label: "Отменена",
    color: "default",
  },
};

const ERROR_CONFIG = {
  401: {
    title: "Необходимо войти в систему",
    subtitle: "Для просмотра заявки необходимо войти в систему.",
  },
  403: {
    title: "Заявка недоступна",
    subtitle:
      "У вас нет доступа к этой заявке или она больше недоступна для просмотра.",
  },
  404: {
    title: "Заявка не найдена",
    subtitle: "Возможно, ссылка устарела или заявка была удалена.",
  },
  400: {
    title: "Некорректный запрос",
    subtitle: "Проверьте правильность ссылки.",
  },
  500: {
    title: "Ошибка сервера",
    subtitle: "Попробуйте позже.",
  },
};

const getRequestTypeName = (request) =>
  request?.form?.name ||
  request?.request_form?.name ||
  request?.type?.name ||
  request?.type ||
  "Не указано";

const getStatusView = (status) => {
  const config = STATUS_CONFIG[status];

  if (!config) return "Не указано";

  return <Tag color={config.color}>{config.label}</Tag>;
};

const DetailRow = ({ label, children }) => (
  <div className={styles.detailRow}>
    <span className={styles.detailLabel}>{label}:</span>
    <span className={styles.detailValue}>{children}</span>
  </div>
);

const RequestEntry = ({ isPublic = false }) => {
  const { uuid } = useParams();
  const { message } = App.useApp();
  const [cancelForm] = Form.useForm();

  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelPending, setCancelPending] = useState(false);

  const statusConfig = STATUS_CONFIG[request?.status];

  const pageTitle = useMemo(() => {
    if (!request) return "Заявка";

    return request.task_number ? `Заявка №${request.task_number}` : "Заявка";
  }, [request]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getRequestEntry({ uuid });
      setRequest(data);
    } catch (error) {
      const status = error?.status;

      if (
        status === 401 ||
        status === 403 ||
        status === 404 ||
        status === 400
      ) {
        setError({ type: String(status) });
      } else if (status >= 500) {
        setError({ type: "500" });
      } else {
        setError({
          type: "generic",
          message: error?.message || "Ошибка загрузки заявки",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, isPublic]);

  const handleCancelClick = () => {
    const canCancelRequest = request?.can_cancel === true;

    if (!canCancelRequest) {
      Modal.warning({
        title: "Отмена невозможна",
        content: "По заявке уже выполняются работы. Отмена заявки невозможна.",
        okText: "Понятно",
      });
      return;
    }

    cancelForm.resetFields();
    setCancelModalOpen(true);
  };

  const handleCancelRequest = async () => {
    try {
      const values = await cancelForm.validateFields();

      setCancelPending(true);
      await cancelMyRequest({
        uuid,
        reason: values.reason || "",
      });

      message.open({
        type: "success",
        content: "Заявка отменена",
      });

      setCancelModalOpen(false);
      cancelForm.resetFields();
      fetchData();
    } catch (error) {
      if (error?.errorFields) return;

      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setCancelPending(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex vertical align="center" className={styles.spin}>
          <Skeleton size="large" active />
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ minHeight: 120, textAlign: "center" }}
        >
          {error.type !== "generic" ? (
            <>
              <Title level={4} type="secondary" style={{ marginBottom: 8 }}>
                {ERROR_CONFIG[error.type]?.title}
              </Title>
              <Text type="secondary">{ERROR_CONFIG[error.type]?.subtitle}</Text>
            </>
          ) : (
            <Text type="danger">{error.message}</Text>
          )}
        </Flex>
      );
    }

    return (
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="flex-start" gap={16} wrap>
          <Flex vertical gap={4}>
            <Title level={2} style={{ margin: 0 }}>
              {pageTitle}
            </Title>
            <Text type="secondary">{getRequestTypeName(request)}</Text>
          </Flex>

          {request?.status !== "completed" && request?.status !== "cancelled" && (
            <Button onClick={handleCancelClick}>Отменить заявку</Button>
          )}
        </Flex>

        <div className={styles.details}>
          <DetailRow label="Тип">{getRequestTypeName(request)}</DetailRow>
          <DetailRow label="Номер">
            {request?.task_number || "Не указано"}
          </DetailRow>
          <DetailRow label="Дата">
            {request?.created_at
              ? formateISODateToMoment(request.created_at)
              : "Не указано"}
          </DetailRow>
          <DetailRow label="Статус">{getStatusView(request?.status)}</DetailRow>
        </div>

        {request?.description && (
          <Paragraph className={styles.description}>
            {request.description}
          </Paragraph>
        )}

        {statusConfig?.label && request?.status === "cancelled" && (
          <Text type="danger">Заявка была отменена</Text>
        )}
      </Flex>
    );
  };

  return (
    <Flex className={styles.page} vertical gap={16}>
      <Flex vertical gap={20} className={styles.inner}>
        {!isPublic && (
          <Breadcrumbs
            items={[
              {
                title: "Главная",
                href: routes.dashboards,
              },
              {
                title: "Мои заявки",
                href: routes.myRequests,
              },
              {
                title: pageTitle,
              },
            ]}
          />
        )}

        <Card variant="borderless" className={styles.card}>
          {renderContent()}
        </Card>
      </Flex>

      <Modal
        maskClosable={false}
        title="Отмена заявки"
        open={cancelModalOpen}
        onOk={handleCancelRequest}
        onCancel={() => setCancelModalOpen(false)}
        okText="Отменить заявку"
        cancelText="Закрыть"
        okButtonProps={{ loading: cancelPending }}
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item name="reason" label="Причина отмены">
            <Input.TextArea rows={4} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

export default RequestEntry;
